import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { ResourceBlock } from 'stile-shared/src/components/2020/blocks/ResourceBlock';
import { Card } from 'stile-shared/src/components/2020/common/Card';

import { theme } from 'styles/theme';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Stile resources"
      description="Browse our teaching resources to elevate teaching and learning experiences. From classroom to lesson planning resources, discover Stile’s teaching aids now."
      slug={props.location.pathname}
    />
  );
}

function ResourcesPage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <HeroBlock
        title="Stile resources"
        text="Resources to elevate your teaching and learning experience"
        image={
          <StaticImage
            loading="eager"
            aspectRatio={2.37}
            src="../../../stile-shared/assets/images/resources/cover_resources_au.png"
            alt="A collection of Stile teaching resource books on a table"
          />
        }
      />

      <ResourceBlock
        title="Useful links"
        text="Get to know the basics of Stile, enrolling students, browsing the library
              and creating your own lessons."
      >
        <Card
          title="Getting Started"
          text="Brand new to Stile? Have a look at these quick tutorials."
          url="https://stileapp.com/go/GetStarted1"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/getting_started.png"
              alt="A closeup of a hand poised above a laptop"
            />
          }
        />

        <Card
          title="Stile Library"
          text="Explore the range of interactive, relevant and engaging science lessons."
          url="https://stileapp.com/library/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/stile_library.png"
              alt="A closeup of a laptop showing the Stile Library"
            />
          }
        />

        <Card
          title="Help centre"
          text="View help articles on all Stile app features."
          url="https://help.stileapp.com"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/help_centre.png"
              alt="A support staff member wearing a call headset and smiling"
            />
          }
        />
      </ResourceBlock>

      <ResourceBlock
        title="Curriculum planning resources"
        text="Get a head start on planning for next year with our curriculum-aligned scope and sequences. These documents are for science leaders and educators to teach a Years 7-10 science program."
      >
        <Card
          title="Australian Curriculum Version 9.0"
          url="/resources/ac9.0-2025/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/ac_9_2025.png" alt="" />
          }
        />

        <Card
          title="Australian Curriculum Version 8.4"
          url="/resources/ac8.4-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../stile-shared/assets/images/resources/ac_8.4_2024.png"
              alt=""
            />
          }
        />

        <Card
          title="New South Wales Syllabus"
          url="/resources/nsw-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/nsw_2024.png" alt="" />
          }
        />

        <Card
          title="New South Wales Syllabus and Implementation Guide"
          url="/resources/new-nsw-syllabus/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/nsw_2026.png" alt="" />
          }
        />

        <Card
          title="Victorian Curriculum"
          url="/resources/vic-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/vic_2024.png" alt="" />
          }
        />

        <Card
          title="Western Australian Curriculum"
          url="/resources/wa-2025/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/wa_2025.png" alt="" />
          }
        />

        <Card
          title="Victorian Curriculum 2.0 Implementation Guide"
          url="/resources/victorian-curriculum-2026/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage src="../../../stile-shared/assets/images/resources/vic_2026.png" alt="" />
          }
        />
      </ResourceBlock>

      <ResourceBlock
        title="Case studies"
        text="We support tens of thousands of teachers around the world. Hear directly from them on the impact Stile is having in their classrooms."
      >
        <Card
          space={theme.space.xxs}
          title="Case study: Stile X at Montmorency Secondary College"
          text="Campbell shares how Stile's comprehensive curriculum is improving student outcomes."
          url="https://stileapp.com/go/stile_x_montmorency_sc"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/tile_montmorency_case_study.png"
              alt=""
            />
          }
        />
        <Card
          space={theme.space.xxs}
          title="Case study: Stile X at Nerang State High School"
          text="Lara talks about how she’s preparing for the new Australian Curriculum with Stile."
          url="https://stileapp.com/go/stile_x_nerang_shs"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/tile_nerang_shs_case_study.png"
              alt=""
            />
          }
        />
        <Card
          space={theme.space.xxs}
          title="Case study: Stile X at Galen Catholic College"
          text="Samuel discusses three ways that Stile enhances science teaching and learning at his school."
          url="https://stileapp.com/go/stile_x_galen_catholic_college"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/tile_galen_case_study.png"
              alt=""
            />
          }
        />
        <Card
          space={theme.space.xxs}
          title="Case study: Stile X at Covenant College"
          text="Nathanael shares how his first semester with Stile X is going."
          url="https://stileapp.com/go/stile_x_covenant_college"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/tile_covenant_case_study.png"
              alt=""
            />
          }
        />
        <Card
          space={theme.space.xxs}
          title="Case study: Stile X at Collingwood College"
          text="Michelle discusses how Stile X helps build her students' skills for the future."
          url="https://stileapp.com/go/stile_x_collingwood_college"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/tile_collingwood_case_study.png"
              alt=""
            />
          }
        />
      </ResourceBlock>

      <ResourceBlock title="Tailored for...">
        <Card
          title="ICT professionals"
          text="Further information for ICT professionals on Stile, including our security and privacy practices and integrations."
          url="/resources/ict-professionals/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/security_privacy_tile.png"
              alt=""
            />
          }
        />
        <Card
          title="Parents and guardians"
          text="Further information for parents and guardians on Stile."
          url="/resources/parents-and-guardians/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/parents_and_guardians_tile.png"
              alt=""
            />
          }
        />
      </ResourceBlock>

      <ResourceBlock title="Other resources">
        <Card
          title="The Stile Guide"
          text="The essential guide to supercharge your science teaching with Stile."
          url="/resources/stile-guide/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/Stile_Guide_tile.jpg"
              alt="A stack of three copies of the Stile Guide"
            />
          }
        />

        <Card
          title="Stile Blog"
          text="Read up on our latest thinking on teaching and learning, cutting-edge science and educational research."
          url="https://blog.stileeducation.com/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/stile_blog_tile.png"
              alt=""
            />
          }
        />

        <Card
          title="Stile Shop"
          text="Check out the latest science teaching and learning resources and fun, practical Stile merchandise!
          "
          url="https://shop.stileeducation.com/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/stile_shop_tile.png"
              alt=""
            />
          }
        />

        <Card
          title="Virtual reality"
          text="View the VR experiences featured in our lessons."
          url="/other/vr/"
          image={
            <StaticImage
              src="../../../stile-shared/assets/images/resources/virtual_reality.png"
              alt="A student holds a VR headset up to their eyes"
            />
          }
        />
      </ResourceBlock>

      <CtaBlock />
    </PageLayout>
  );
}

export default ResourcesPage;
